import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, ClipboardList, FileWarning, FlaskConical, HardHat, CheckCircle2, BellRing } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { http } from '../api';
import { useAuth } from '../auth/AuthContext';

const ICON = { os_overdue: ClipboardList, ces_pending: FileWarning, low_stock: FlaskConical, sst_expiry: HardHat, funnel_digest: BellRing };
const TONE = { os_overdue: 'text-rose-600 bg-rose-50', ces_pending: 'text-amber-700 bg-amber-50', low_stock: 'text-sky-700 bg-sky-50', sst_expiry: 'text-violet-700 bg-violet-50', funnel_digest: 'text-emerald-700 bg-emerald-50' };

export function NotificationBell() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'superadmin'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const load = useCallback(() => http.get('/app-settings/alerts').then(r => setItems(r.data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', h);
    return () => document.removeEventListener('pointerdown', h);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button aria-label="Notificações" data-testid="app-header-bell" onClick={() => { setOpen(o => !o); if (!open) load(); }} className="relative p-2 hover:bg-slate-100 rounded-lg">
        <Bell className="w-5 h-5 text-slate-700" />
        {items.length > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="app-header-bell-count">{items.length > 9 ? '9+' : items.length}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[min(22rem,calc(100vw-2rem))] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="notifications-panel">
          <div className="px-4 py-3 border-b border-slate-100 text-[13px] font-bold text-slate-900">Notificações</div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
            {items.length === 0 && <div className="px-4 py-8 text-center text-[12px] text-slate-500 flex flex-col items-center gap-2" data-testid="notifications-empty"><CheckCircle2 className="w-6 h-6 text-emerald-500" />Nenhum aviso no momento.</div>}
            {items.map((n, i) => {
              const Icon = ICON[n.kind] || Bell;
              return (
                <button key={i} type="button" onClick={() => { setOpen(false); navigate(n.url); }} className="w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50" data-testid={`notification-item-${i}`}>
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${TONE[n.kind] || 'bg-slate-100 text-slate-600'}`}><Icon className="w-4 h-4" /></span>
                  <span className="min-w-0"><span className="block text-[12px] font-semibold text-slate-900">{n.title}</span><span className="block text-[11px] text-slate-500">{n.body}</span></span>
                </button>
              );
            })}
          </div>
          {isAdmin && <button type="button" onClick={() => { setOpen(false); navigate('/configuracoes'); }} className="w-full px-4 py-2.5 border-t border-slate-100 text-[11px] font-semibold text-emerald-700 hover:bg-slate-50" data-testid="notifications-settings-link">Escolher quais avisos aparecem</button>}
        </div>
      )}
    </div>
  );
}
